// frontend/src/features/livestock/useTankParam.ts
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { useTank } from '@/contexts/TankContext';
import { useOpenInTank } from './useOpenInTank';

export interface TankFromId {
  /**
   * The wanted tank is being checked or selected: the page shows the
   * previously selected tank until then, so it should wait.
   */
  switching: boolean;
  /**
   * Nothing is pending for the wanted tank: it was applied (selected, or found
   * unknown and said so) and no selection is under way. Read at render.
   */
  settled: boolean;
}

/**
 * The shared core of `useTankParam` and `useTankFromPath`: a tank id from the
 * URL (`?tank=` or the path) selects that tank once
 * (docs/plans/2026-10-29-livestock-compare-plan.md, 5.2):
 * - One of the user's other tanks is selected once, through `useOpenInTank` as
 *   the other deep links do, staying on the current URL (replacing it).
 * - An id not in the (possibly persisted, stale) tanks list is checked against
 *   a fresh list first; still unknown, it says so and leaves the selection alone.
 */
export function useTankFromId(
  wanted: string | null,
  /** The unknown-tank notice says the selected tank shows instead (pages that show it). */
  sayShowingInstead = true
): TankFromId {
  const { tanks, selectedTank, refreshTanks } = useTank();
  const [params] = useSearchParams();
  const { pathname } = useLocation();
  const openInTank = useOpenInTank();
  const applied = useRef<string | null>(null);
  const [switching, setSwitching] = useState(false);
  // The unknown tank id whose fresh tanks list has come back (and the one being fetched).
  const [refreshedFor, setRefreshedFor] = useState<string | null>(null);
  const refreshingFor = useRef<string | null>(null);

  const known = !!wanted && tanks.some((tank) => tank.id === wanted);
  const selectedId = selectedTank?.id;
  const selectedName = selectedTank?.name;

  useEffect(() => {
    if (!wanted || applied.current === wanted || tanks.length === 0) return;
    if (!known) {
      if (refreshedFor !== wanted) {
        if (refreshingFor.current !== wanted) {
          refreshingFor.current = wanted;
          void Promise.resolve(refreshTanks?.())
            .catch(() => undefined)
            .finally(() => setRefreshedFor(wanted));
        }
        return;
      }
      applied.current = wanted;
      // Checked: coming back to this tank later (after another) checks it again.
      refreshingFor.current = null;
      notifications.show({
        id: 'tank-param-unknown',
        color: 'orange',
        title: "That tank isn't in your account",
        message: sayShowingInstead && selectedName ? `Showing ${selectedName} instead.` : undefined,
      });
      setRefreshedFor(null);
      return;
    }
    applied.current = wanted;
    if (selectedId === wanted) return;
    setSwitching(true);
    // The same page, with ?tank= (when it has one) naming the tank being selected.
    const next = new URLSearchParams(params);
    if (next.has('tank')) next.set('tank', wanted);
    const query = next.toString();
    void openInTank(wanted, `${pathname}${query ? `?${query}` : ''}`, { replace: true }).finally(
      () => setSwitching(false)
    );
    // Once per tank in the URL: params, pathname and the selection change as it applies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wanted, known, tanks.length, selectedId, refreshedFor, openInTank]);

  return {
    switching:
      switching ||
      (!!wanted && tanks.length > 0 && applied.current !== wanted && selectedId !== wanted),
    settled: !switching && (!wanted || applied.current === wanted),
  };
}

/**
 * Keeps a page's `?tank=` and the selected tank together, so a link or bookmark
 * opens the right tank (Tank Shots, design 6.2):
 * - A `?tank=` of one of the user's other tanks selects that tank once, through
 *   `useOpenInTank` as the other deep links do.
 * - A `?tank=` not in the (possibly persisted, stale) tanks list is checked
 *   against a fresh list first; still unknown, it says so and shows the selected tank.
 * - Otherwise the URL follows the selection: no `?tank=`, an unknown one, or a
 *   switch in the tank picker afterwards rewrite it (replacing the history entry).
 *
 * Returns true while the tank in the URL is being checked or selected: the page
 * shows the previously selected tank until then, so it should wait.
 */
export function useTankParam(): boolean {
  const { selectedTank } = useTank();
  const [params, setParams] = useSearchParams();
  const wanted = params.get('tank');
  const { switching, settled } = useTankFromId(wanted);
  const selectedId = selectedTank?.id;

  useEffect(() => {
    // A tank in the URL that is still to be checked or applied is not overwritten.
    if (!settled || !selectedId || wanted === selectedId) return;
    const next = new URLSearchParams(params);
    next.set('tank', selectedId);
    setParams(next, { replace: true });
  }, [settled, selectedId, wanted, params, setParams]);

  return switching;
}

/**
 * The tank in a page's path (`/tanks/:tankId/...`, the organism compare page)
 * and the selected tank: the path's tank is selected once, as `?tank=` is
 * (decision 10). A later switch in the tank picker, away from the path's tank
 * once it was selected, goes to `/livestock`: the organism isn't in the other
 * tank. An unknown tank leaves the selection alone (the page's own read fails).
 *
 * Returns true while the path's tank is being checked or selected.
 */
export function useTankFromPath(tankId: string | undefined): boolean {
  const { selectedTank } = useTank();
  const navigate = useNavigate();
  const wanted = tankId ?? null;
  // The page doesn't show the selected tank instead: it says the organism isn't here.
  const { switching } = useTankFromId(wanted, false);
  const selectedId = selectedTank?.id;
  // The path's tank has been the selected one (since this path was opened).
  const reached = useRef<string | null>(null);

  useEffect(() => {
    if (!wanted || !selectedId) return;
    if (selectedId === wanted) {
      reached.current = wanted;
      return;
    }
    if (reached.current === wanted && !switching) {
      reached.current = null;
      // In place of this page: Back mustn't return to it and select its tank again
      navigate('/livestock', { replace: true });
    }
  }, [wanted, selectedId, switching, navigate]);

  return switching;
}
